import React from 'react';
import { FetchResult } from '@apollo/client';

import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import ToggleSwitch from 'buyside/components/presentation/ToggleSwitch/toggleSwitch';

import {
  StripeSubscriptionQuery,
  useStripeSubscriptionQuery,
  useUpdateSubscriptionMutation
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/UserAccount/BillingInformation/Subscription/subscriptionStyles.scss';

const Subscription: React.FC = (): React.ReactElement | null => {
  const { error, loading, data: currentUserData } = useStripeSubscriptionQuery();

  const [
    updateSubscription,
    { loading: updateSubscriptionLoading }
  ] = useUpdateSubscriptionMutation({
    update: (cache, { data }) => {
      const subscription = data?.updateSubscription?.subscription;

      if (!subscription) return;

      cache.modify<NonNullable<StripeSubscriptionQuery['currentUser']>>({
        id: cache.identify({ id: currentUserData?.currentUser?.id, __typename: 'User' }),
        fields: {
          subscriptions: (cachedData, { readField }) => {
            if (!cachedData) return [subscription];
            const otherSubscriptions = cachedData.filter(
              cachedSub => readField('id', cachedSub) !== subscription.id
            );
            return [...otherSubscriptions, subscription];
          }
        }
      });
    }
  });

  if (error) throw error;
  if (loading) return <LoadingSpinner size={SIZES.TINY} />;
  if (!currentUserData) return null;

  const subscriptions = currentUserData.currentUser?.subscriptions;

  if (!subscriptions?.length) return <div>No subscriptions.</div>;

  return (
    <table>
      <tbody>
        {subscriptions.map(subscription => {
          if (!subscription) return null;

          return (
            <tr className={styles.subscriptionTypeContainer} key={subscription.id}>
              <th>{subscription.description}</th>

              <td className={styles.toggleSwitchContainer}>
                <ToggleSwitch
                  checked={subscription.isAutoRenewing}
                  onChange={(): Promise<FetchResult> =>
                    updateSubscription({ variables: { id: subscription.id } })
                  }
                  isLoading={updateSubscriptionLoading}
                />
              </td>

              <td className={styles.renewalDateInformation}>
                {subscription?.isAutoRenewing
                  ? 'Your next billing date for this plan is '
                  : 'Your access for this plan will end on '}
                <strong>{subscription.currentPeriodEnd}</strong>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default Subscription;
